import { Injectable } from '@angular/core';
import { AdminService } from '@mypxplat/xplat/core';
import { AppService } from '@mypxplat/xplat/web/core';
import { BaseContent, ContentData } from 'libs/xplat/core/src/lib/models/admin.model';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class ProductContentService {

  constructor(
    private adminService: AdminService,
    private appService: AppService,
    private spinner: NgxSpinnerService,
    private translate: TranslateService
  ) {}

  /**
   * Save content for a single product (product detail page context)
   */
  saveForSingleProduct(contentData: any): Observable<any> {
    return this.adminService.addUpdateProductContent(contentData);
  }

  /**
   * Save content for multiple products (content packages manager context)
   */
  saveForMultipleProducts(contentData: BaseContent, productIds: number[], removedProductIds?: number[]): Observable<any> {
    return this.adminService.modifyProductContentAssociation(productIds, contentData, removedProductIds);
  }

  /**
   * Add product association using existing addUpdateProductContent endpoint
   */
  addProductAssociation(productId: number, contentData: any): Observable<any> {
    return this.adminService.addUpdateProductContent({
      ...contentData,
      product_id: productId,
      id: null,
    });
  }

  /**
   * Remove product associations by sending removedProductIds via the PUT endpoint
   * This avoids URL encoding issues that occurred with the DELETE endpoint
   */
  removeProductAssociation(contentData: BaseContent, removedProductIds: number[]): Observable<any> {
    return this.adminService.modifyProductContentAssociation([], contentData, removedProductIds);
  }

  /**
   * Build standardized content data object
   */
  buildContentData(data: any): ContentData {
    const content: ContentData & { content_package_ids?: string[] } = {
      type: data.type || 'content',
      stringId: data.stringId || '',
      title: data.title || '',
      description: data.description || '',
      version: data.version || '',
      releaseDate: data.releaseDate || '',
      mimeType: data.mimeType || '',
      contentLength: data.contentLength ? parseInt(data.contentLength) : undefined,
      osRequirement: data.osRequirement || '',
      downloadSection: data.downloadSection || '',
      cdnUrl: data.cdnUrl || '',
      beta: data.beta ?? null,
      folder: data.folder || '',
      flags: data.flags || '',
      serialCutin: data.serialCutin || '',
      signature: data.signature || '',
      fileName: data.fileName,
      DTYPE: data.DTYPE
    };
    if (data.content_package_ids?.length) {
      content.content_package_ids = data.content_package_ids;
    }
    return content;
  }

  /**
   * Build product image arguments for upload
   */
  buildProductImageArgs(data: any, productId: number): any {
    return {
      DTYPE: "ContentPackage",
      fileName: data.fileName,
      contentLength: data.contentLength,
      folder: "pictures",
      title: data.fileName,
      stringId: "bigicon",
      downloadSection: "no_download",
      product_id: productId,
      type: "content",
    };
  }

  /**
   * Build product sample (mp3) arguments for upload
   */
  buildProductSampleArgs(data: any, productId: number): any {
    return {
      DTYPE: "ContentPackage",
      fileName: data.fileName,
      contentLength: data.contentLength,
      folder: productId ? `samples/${productId}` : "samples",
      title: data.title || data.fileName,
      stringId: data.stringId || "sample",
      downloadSection: "samples",
      mimeType: "audio/mpeg",
      product_id: productId,
      type: "audio",
    };
  }

  /**
   * Handle API errors consistently
   */
  handleError(error: any, operation: string): void {
    console.error(`Error ${operation}:`, error);
    this.appService.alertError(error);
  }

  /**
   * Show success message
   */
  showSuccessMessage(message: string): void {
    alert(message);
  }

  /**
   * Show loading spinner
   */
  showLoading(): void {
    this.spinner.show();
  }

  /**
   * Hide loading spinner
   */
  hideLoading(): void {
    this.spinner.hide();
  }

  /**
   * Get changes between initial and current product selections
   */
  getProductAssociationChanges(
    initialIds: Set<number>, 
    currentIds: number[]
  ): { added: number[], removed: number[] } {
    const currentIdsSet = new Set(currentIds);
    const added = Array.from(currentIdsSet).filter(id => !initialIds.has(id));
    const removed = Array.from(initialIds).filter(id => !currentIdsSet.has(id));
    
    return { added, removed };
  }

  /**
   * Validate content data before saving
   */
  validateContentData(data: any): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!data.title) errors.push('validation.title_required');
    if (!data.folder) errors.push('validation.folder_required');
    if (!data.fileName) errors.push('validation.filename_required');
    if (!data.product_id && !data.selectedProductIds?.length) {
      errors.push('validation.products_required');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}
